import { DragOutlined, FilterOutlined, FullscreenOutlined, LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { Badge, Button, Modal, Spin, SpinProps } from "antd";
import { DownloadButtonWithModal } from "components/UI/DownloadButtonWithModal/DownloadButtonWithModal";
import { AddEntityData } from "components/UI/Table";
import { dragAndDropEnabledAtom } from "components/UI/Table/atoms/dragAndDropAtom";
import { ExportRange } from "components/UI/Table/atoms/exportAtom";
import { useTableUrlParameters } from "components/UI/Table/hooks/useTableUrlParameters";
import { DateFilterMode, QuickFilterOption, TableColumnType } from "components/UI/Table/types/table";
import { AddButton } from "components/UI/Table/UI/AddButton/AddButton";
import { ColumnVisibility } from "components/UI/Table/UI/ColumnVisibility/ColumnVisibility";
import { Filter } from "components/UI/Table/UI/Filter";
import { QuickFilterPills } from "components/UI/Table/UI/QuickFilterPills/QuickFilterPills";
import { QuickSearch } from "components/UI/Table/UI/QuickSearch/QuickSearch";
import { TimeFilter } from "components/UI/Table/UI/TimeFilter/TimeFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { SearchFilterEnum } from "types/enums";
import { Identifiable, TableId } from "types/models";
import { zIndexes } from "utils/zIndexes";

interface MyTopBarProps<T> {
    columnConfig: TableColumnType<T>[];
    tableId: TableId;
    onRefresh?: () => void;
    showRefetchButton?: boolean;
    enableTimeFilter?: boolean;
    enableFilters?: boolean;
    loading?: boolean | SpinProps;
    enableColumnsVisibility?: boolean;
    enableDragAndDrop?: boolean;
    addEntityData?: AddEntityData;
    onExport?: () => Promise<string | undefined>;
    exportRangeOptions?: { value: ExportRange; label: string }[];
    dateFilterDataIndex?: string;
    dateFilterMode?: DateFilterMode;
    searchDataIndex?: string;
    searchOperator?: SearchFilterEnum;
    searchMinLength?: number;
    searchDebounceMs?: number;
    searchPlaceholder?: string;
    quickFilterDataIndex?: string;
    quickFilterOptions?: QuickFilterOption[];
    quickFilterOperator?: SearchFilterEnum;
    extraIcons?: React.ReactNode;
    extraButtons?: React.ReactNode;
    renderTable?: () => React.ReactNode;
    allowExpand?: boolean;
    expandButtonRef?: React.RefObject<() => void>;
    extraFilters?: Array<{
        dataIndex: string;
        title: string;
        filterType: import("../../types/filter").FilterType;
        filterOptions?: { label: string; value: string | number | boolean }[];
        filterName?: string;
    }>;
}

export const TopBar = <T extends Identifiable>({
    columnConfig,
    tableId,
    onRefresh,
    showRefetchButton,
    enableTimeFilter,
    enableFilters,
    loading,
    enableColumnsVisibility,
    enableDragAndDrop = true,
    addEntityData,
    onExport,
    exportRangeOptions,
    dateFilterDataIndex,
    dateFilterMode,
    searchDataIndex,
    searchOperator,
    searchMinLength,
    searchDebounceMs,
    searchPlaceholder,
    quickFilterDataIndex,
    quickFilterOptions,
    quickFilterOperator,
    extraIcons,
    extraButtons,
    renderTable,
    allowExpand,
    expandButtonRef,
    extraFilters,
}: MyTopBarProps<T>) => {
    const [expandModal, setExpandModal] = useState(false);
    const [dragAndDropEnabled, setDragAndDropEnabled] = useRecoilState(dragAndDropEnabledAtom(tableId));
    const permissionsUtils = usePermission();
    const tableUrlParametersUtils = useTableUrlParameters<T>(tableId);
    const { t: tC } = usePaldockTranslation("common");

    // Expose the expand modal trigger function via ref
    useEffect(() => {
        if (expandButtonRef && expandButtonRef.current !== undefined) {
            (expandButtonRef as any).current = () => setExpandModal(true);
        }
    }, [expandButtonRef]);

    const fullFilter = tableUrlParametersUtils.getUrlParameters()?.filter?.filter ?? {};

    const filterWithoutTimeFilter =
        enableTimeFilter && dateFilterDataIndex
            ? Object.fromEntries(Object.entries(fullFilter).filter(([key]) => key !== dateFilterDataIndex))
            : fullFilter;
    const appliedFilterCount = Object.keys(filterWithoutTimeFilter).length;

    // Don't auto-open the Filter dropdown just because the quick-search box wrote its own
    // column into the URL filter block (e.g. from a shared/refreshed link) — that's not a
    // manual filter the user opened the dropdown to set.
    const quickFilterKeys = [searchDataIndex, quickFilterDataIndex].filter(Boolean);
    const filterForAutoOpen = Object.fromEntries(
        Object.entries(filterWithoutTimeFilter).filter(([key]) => !quickFilterKeys.includes(key))
    );
    const [isFilterOpen, setIsFilterOpen] = useState(Object.keys(filterForAutoOpen).length > 0);

    const toggleFilterBox = () => setIsFilterOpen((prev) => !prev);

    if (enableTimeFilter && !dateFilterDataIndex) {
        // TODO - refactor timeFilter as object including enabled, dataIndex
        throw new Error("enableTimeFilter is enabled, but dateFilterDataIndex missing.");
    }

    return (
        <div className={`bg-white top-0 z-${zIndexes.tableTopBar} rounded`}>
            <div className="p-4 relative">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {enableTimeFilter && dateFilterDataIndex && (
                            <TimeFilter
                                tableId={tableId}
                                dataIndex={dateFilterDataIndex}
                                dateFilterMode={dateFilterMode}
                            />
                        )}
                        {enableFilters && (
                            <Badge count={appliedFilterCount} color="#008080">
                                <Button icon={<FilterOutlined />} onClick={toggleFilterBox}>
                                    {tC("filter")}
                                </Button>
                            </Badge>
                        )}
                        {searchDataIndex && (
                            <QuickSearch
                                tableId={tableId}
                                dataIndex={searchDataIndex}
                                operator={searchOperator}
                                minLength={searchMinLength}
                                debounceMs={searchDebounceMs}
                                placeholder={searchPlaceholder}
                            />
                        )}
                        {quickFilterDataIndex && quickFilterOptions && (
                            <QuickFilterPills
                                tableId={tableId}
                                dataIndex={quickFilterDataIndex}
                                options={quickFilterOptions}
                                operator={quickFilterOperator}
                            />
                        )}
                        {extraButtons}
                    </div>

                    <div className="flex items-center gap-3">
                        {extraIcons}

                        {onExport && (
                            <DownloadButtonWithModal
                                onExport={onExport}
                                withText={false}
                                size="small"
                                type="link"
                                tableId={tableId}
                                rangeOptions={exportRangeOptions}
                            />
                        )}

                        {onRefresh && showRefetchButton && (
                            <>
                                {loading !== undefined ? (
                                    loading === true ? (
                                        <Button onClick={onRefresh} size="small" type="link" className="!text-black">
                                            <Spin indicator={<LoadingOutlined spin />} size="small" />
                                        </Button>
                                    ) : (
                                        <Button onClick={onRefresh} size="small" type="link" className="!text-black">
                                            <SyncOutlined />
                                        </Button>
                                    )
                                ) : (
                                    <Button onClick={onRefresh} size="small" type="link" className="!text-black">
                                        <SyncOutlined />
                                    </Button>
                                )}
                            </>
                        )}

                        {enableDragAndDrop && (
                            <Button
                                onClick={() => setDragAndDropEnabled(!dragAndDropEnabled)}
                                size="small"
                                type={"link"}
                                className="!text-black"
                                icon={
                                    <DragOutlined
                                        style={{
                                            color: dragAndDropEnabled ? "red" : "black",
                                        }}
                                    />
                                }
                            />
                        )}

                        {enableColumnsVisibility && <ColumnVisibility tableId={tableId} />}

                        {addEntityData &&
                            (!addEntityData.permission || permissionsUtils.hasPermission(addEntityData.permission)) && (
                                <AddButton drawerId={addEntityData.drawerId} />
                            )}

                        {allowExpand && (
                            <Button onClick={() => setExpandModal(true)} size={"small"}>
                                <FullscreenOutlined /> {tC("expand")}
                            </Button>
                        )}
                    </div>
                </div>
                {isFilterOpen && enableFilters && (
                    <Filter columnConfig={columnConfig} tableId={tableId} extraFilters={extraFilters} />
                )}
                {expandModal && (
                    <Modal
                        open={expandModal}
                        onCancel={() => setExpandModal(false)}
                        width="90vw"
                        footer={null}
                        classNames={{ content: "h-[90vh] w-full" }}
                        styles={{ body: { height: "100%", overflow: "auto" } }}
                    >
                        {renderTable ? renderTable() : null}
                    </Modal>
                )}
            </div>
        </div>
    );
};
